const mergeDefined = (base = {}, value = {}) => {
  let result = { ...base }
  Object.keys(value).forEach((key) => {
    if (value[key] !== undefined) {
      result[key] = value[key]
    }
  })
  return result
}

export const shouldKeepTabRender = (
  uischema = {},
  hasSharedTabUi = false,
) =>
  uischema.isSameUi !== true &&
  !hasSharedTabUi &&
  (uischema.isKeepTabRenderTouched === true
    ? uischema.isKeepTabRender === true
    : true)

export const getTabActiveRenderKey = (
  uischema = {},
  activeTabKey = '',
  defaultActiveKey = '',
) => (uischema.isSameUi ? defaultActiveKey : activeTabKey)

export const getTabElementKey = (element = {}, index = 0) => {
  return (
    element.key ||
    element.name ||
    element.scope ||
    element.title ||
    element.label ||
    element.fieldName ||
    `tab-${index}`
  )
}

export const getTabActiveKey = (uischema = {}, activeTab = '') => {
  let elements = uischema.elements || []
  let activeIndex = elements.findIndex(
    (element, index) =>
      getTabElementKey(element, index) === activeTab ||
      element.name === activeTab,
  )

  return activeIndex > -1
    ? getTabElementKey(elements[activeIndex], activeIndex)
    : elements.length > 0
    ? getTabElementKey(elements[0])
    : ''
}

export const createTabActiveState = (state = {}, payload = {}) => {
  let {
    tabKey,
    tabKeys,
    tabPanelName,
    filterParam = [],
    shouldSetParam = false,
    tabState,
    clearTabState = false,
  } = payload
  let activeTabKeys = [...new Set((tabKeys || [tabKey]).filter(Boolean))]
  if (activeTabKeys.length === 0) return state

  let activeTab = { ...(state.isUiChanged?.activeTab || {}) }
  let hasUiChanged = false
  if (tabPanelName) {
    activeTabKeys.forEach((activeTabKey) => {
      if (activeTab[activeTabKey] === tabPanelName) return
      activeTab[activeTabKey] = tabPanelName
      hasUiChanged = true
    })
  }
  let nextIsUiChanged = {
    ...state.isUiChanged,
    activeTab,
  }
  if (clearTabState) {
    activeTabKeys.forEach((activeTabKey) => {
      let currentTabState = { ...(nextIsUiChanged[activeTabKey] || {}) }
      if (
        currentTabState.tabKey === undefined &&
        currentTabState.tabPanelUi === undefined
      ) {
        return
      }
      delete currentTabState.tabKey
      delete currentTabState.tabPanelUi
      nextIsUiChanged[activeTabKey] = currentTabState
      hasUiChanged = true
    })
  }
  if (tabState) {
    activeTabKeys.forEach((activeTabKey) => {
      let currentTabState = nextIsUiChanged[activeTabKey] || {}
      let hasTabStateChanged = Object.keys(tabState).some(
        (key) => currentTabState[key] !== tabState[key],
      )
      if (!hasTabStateChanged) return
      nextIsUiChanged[activeTabKey] = { ...currentTabState, ...tabState }
      hasUiChanged = true
    })
  }

  let nextState = state
  if (shouldSetParam) {
    let tabParamActive = { ...state.tabParamActive }
    let hasParamChanged = false
    activeTabKeys.forEach((activeTabKey) => {
      let currentParam = tabParamActive[activeTabKey] || []
      let isSameParam =
        currentParam.length === filterParam.length &&
        currentParam.every((param, index) => param === filterParam[index])
      if (isSameParam) return
      tabParamActive[activeTabKey] = filterParam
      hasParamChanged = true
    })
    if (hasParamChanged) {
      nextState = { ...nextState, tabParamActive }
    }
  }
  if (hasUiChanged) {
    nextState = { ...nextState, isUiChanged: nextIsUiChanged }
  }
  return nextState
}

export const createTabPaneUi = (element, index = 0) => {
  if (!element) return element

  let paneKey = getTabElementKey(element, index)
  let paneTitle = element.title || element.label || element.fieldName

  if (
    element.key === paneKey &&
    element.title === paneTitle
  ) {
    return element
  }

  let paneUi = {
    ...element,
    key: paneKey,
    title: paneTitle,
  }

  return paneUi
}

export const mergeTabUiElement = (element, fallbackElement) => {
  if (!element) return fallbackElement
  if (!fallbackElement) return element
  if (element.key !== fallbackElement.key) return element

  let result = mergeDefined(fallbackElement, element)
  let fallbackElements = fallbackElement.elements || []
  let currentElements = element.elements || []

  if (fallbackElements.length > 0) {
    result.elements = fallbackElements.map((fallbackChild) => {
      let currentChild = currentElements.find(
        ({ key }) => key === fallbackChild.key
      )
      return mergeTabUiElement(currentChild, fallbackChild)
    })

    currentElements.forEach((currentChild) => {
      let hasChild = result.elements.find(({ key }) => key === currentChild.key)
      if (!hasChild) {
        result.elements.push(currentChild)
      }
    })
  }

  return result
}

export const resolveTabUiElement = (
  key,
  element,
  fallbackElement,
  fallbackIndex = 0
) => {
  if (!element) return fallbackElement
  if (!fallbackElement) return element
  if (element === fallbackElement) return element

  let elementKey = getTabElementKey(element, fallbackIndex)
  let fallbackKey = getTabElementKey(fallbackElement, fallbackIndex)

  if (elementKey !== key && fallbackKey === key) {
    return fallbackElement
  }

  return mergeTabUiElement(element, fallbackElement)
}
